import type { CoreError } from '@ingenico/api-sdk-core/common';
import type { CategoryEnum, CGCustomComponents, CGInputs } from '@ingenico/react-configuration-generator';
import { Banner } from '@ingenico/react-ui/banner';
import { Container } from '@ingenico/react-ui/container';
import { NoticeCard } from '@ingenico/react-ui/notice-card';
import type { DeepPartialBy } from '@ingenico/util-common';
import { LinearProgress } from '@mui/material';
import deepEqual from 'fast-deep-equal';
import type { ReactElement } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConfigurationTab.module.scss';
import { getConfigurationError } from './ConfigurationTab.util';
import { EmptyConfiguration } from './empty-configuration/EmptyConfiguration';
import { useConfigurationTabVm } from './useConfigurationTab.vm';
import { useDeviceManagementError } from '../../../domain/hooks';
import { LNGS_NAMESPACE_CONFIGURATION_PROFILE } from '@ingenico/util-i18n-config';
import { DeviceManagementConfigurationProvider } from '../configuration-form-builder-provider/ConfigurationFormBuilderProvider.ctrl';

export interface ConfigurationTabProps {
  readonly configurationError: CoreError | null;
  readonly inputs: DeepPartialBy<
    CGInputs,
    'actions' | 'fgComponents' | 'fgTranslations' | 'isEditEnabled' | 'translations'
  >;
  readonly isConfigurationLoading: boolean;
  readonly isEmptyConfiguration: boolean;
  readonly selectedCategory?: CategoryEnum;
  readonly selectedApplicationId?: string;
}

export function ConfigurationTabCpnt({
  inputs,
  isConfigurationLoading,
  isEmptyConfiguration,
  configurationError,
  selectedCategory,
  selectedApplicationId,
}: ConfigurationTabProps): ReactElement {
  const { t } = useTranslation([LNGS_NAMESPACE_CONFIGURATION_PROFILE.DEFAULT]);

  const { handleRef } = useConfigurationTabVm({
    inputs,
    selectedCategory,
    selectedApplicationId,
  });
  const { getErrorMessage } = useDeviceManagementError();

  const getNotFoundBanner = useCallback((message: string, title: string) => {
    return (
      <Container>
        <NoticeCard image="not-found" isBordered message={message} title={title} />
      </Container>
    );
  }, []);

  const getBuilderErrorComponent = useCallback(
    (error: CoreError) => {
      switch (error?.status) {
        case 404: {
          return getNotFoundBanner(
            getConfigurationError(t, error),
            t('title.noConfigurationFound', { ns: LNGS_NAMESPACE_CONFIGURATION_PROFILE.DEFAULT }),
          );
        }
        default: {
          return (
            <div className={styles.errorBannerContainer}>
              <Banner message={getConfigurationError(t, error)} />
            </div>
          );
        }
      }
    },
    [getNotFoundBanner, t],
  );

  const customComponents = useMemo<CGCustomComponents>(
    () => ({
      getErrorBanner: (error?: CoreError) => (error ? getBuilderErrorComponent(error) : undefined),
    }),
    [getBuilderErrorComponent],
  );

  const getConfigurationErrorComponent = useCallback(
    (error: CoreError) => {
      switch (error?.status) {
        case 404: {
          return getNotFoundBanner(
            getConfigurationError(t, error),
            t('title.noConfigurationFound', { ns: LNGS_NAMESPACE_CONFIGURATION_PROFILE.DEFAULT }),
          );
        }
        default: {
          return (
            <div className={styles.errorBannerContainer}>
              <Banner message={getErrorMessage(error)} />
            </div>
          );
        }
      }
    },
    [getErrorMessage, getNotFoundBanner, t],
  );

  if (isConfigurationLoading) {
    return <LinearProgress />;
  } else if (configurationError) {
    return getConfigurationErrorComponent(configurationError);
  } else if (isEmptyConfiguration) {
    return <EmptyConfiguration />;
  } else {
    return (
      <DeviceManagementConfigurationProvider
        inputs={inputs}
        initialCategory={selectedCategory}
        customComponents={customComponents}
        ref={handleRef}
      />
    );
  }
}

export const ConfigurationTab = memo(ConfigurationTabCpnt, deepEqual);
